import { SidebarMenu } from "components";
import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    CircularProgress,
    Button,
    AppBar,
    Toolbar,
    IconButton,
    Chip,
    Stack,
    Avatar,
    Paper,
    Divider,
    Link as MuiLink,
} from "@mui/material";
import {
    Person as PersonIcon,
    ArrowBack as ArrowBackIcon,
    LocationOn as LocationOnIcon,
    Phone as PhoneIcon,
    Business as BusinessIcon,
    Map as MapIcon,
} from "@mui/icons-material";
import CardRating from "components/on-six/CardRating";
import { useNavigate } from "react-router-dom";

type RatingItem = {
    id: string;
    restaurantId?: string;
    restaurantName?: string;
    score: number;
    comment?: string;
    createdAt?: string;
    [key: string]: any;
};

const mockRatings: RatingItem[] = [
    {
        id: "r1",
        restaurantId: "REST001",
        restaurantName: "La Buena Mesa",
        score: 5,
        comment: "Excelente servicio y comida deliciosa.",
        createdAt: "2025-11-26T15:24:00Z",
    },
    {
        id: "r2",
        restaurantId: "REST002",
        restaurantName: "El Fogón",
        score: 4,
        comment: "Muy buen lugar, atención amable.",
        createdAt: "2025-11-25T11:12:00Z",
    },
    {
        id: "r3",
        restaurantId: "REST003",
        restaurantName: "Mar y Tierra",
        score: 3,
        comment: "Comida aceptable, tiempo de espera largo.",
        createdAt: "2025-11-24T18:05:00Z",
    },
    {
        id: "r4",
        restaurantId: "REST004",
        restaurantName: "La Esquina Verde",
        score: 2,
        comment: "Platos fríos y presentación pobre.",
        createdAt: "2025-11-23T09:30:00Z",
    },
    {
        id: "r5",
        restaurantId: "REST005",
        restaurantName: "Sabor Tradicional",
        score: 1,
        comment: "Muy mala experiencia, no regresaría.",
        createdAt: "2025-11-22T20:45:00Z",
    },
    {
        id: "r6",
        restaurantId: "REST006",
        restaurantName: "Café Central",
        score: 4,
        comment: "Buen café y ambiente agradable.",
        createdAt: "2025-11-21T08:10:00Z",
    },
];

const OnSixCostumer = () => {
    const navigate = useNavigate();
    const [client, setClient] = useState<any | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [ratings, setRatings] = useState<any[]>(mockRatings);

    const stats = useMemo(() => {
        const totalVisits = ratings.length;
        const sum = ratings.reduce((s, r) => s + (typeof r.score === "number" ? r.score : 0), 0);
        const avgScore = totalVisits > 0 ? sum / totalVisits : 0;
        const lowest = totalVisits > 0 ? ratings.reduce((min, r) => (r.score < (min.score ?? Infinity) ? r : min), ratings[0]) : null;
        return { totalVisits, avgScore, lowest };
    }, [ratings]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [loadingRatings, setLoadingRatings] = useState<boolean>(false);

    const fallbackJson = {
        CardCode: "CL02365",
        CardName: "GALO DE MENDOZA, ANA CRISTINA",
        CardType: "C",
        GroupCode: 104,
        CmpPrivate: "C",
        Address: "FINAL C GRIMALDI Y 12 AV NTE #1 SALIDA A SAN MIGUEL, C A STA MARIA, USULUTAN",
        Phone1: "78344952",
        CntctPrsn: "DUI 01002976 6",
        Notes: "SUPERMERCADO",
        Balance: 69.7,
        CardFName: "SUPER GALO",
        U_NIT: "11232109490015",
        U_canal: ["of-otrascadenas", "of-otrascadenas"],
        U_seg_cli: "Supermercados",
        PymntGroup: "CONTADO",
        BPLLat: "13.3435184200072",
        BPLLon: "-88.4310021056706",
        U_Ruta: "R003",
        BloqueoVentas: "Y",
        enabled: false,
        label: " CL02365 GALO DE MENDOZA, ANA CRISTINA SUPER GALO",
    };

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem("onsix_selected_client");
            if (raw) {
                setClient(JSON.parse(raw));
                return;
            }
        } catch (e) { }
        setClient(fallbackJson);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const mapsHref =
        client?.BPLLat && client?.BPLLon
            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${client.BPLLat},${client.BPLLon}`)}`
            : null;

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "grey.50" }}>
            {/* Sidebar */}
            <Box sx={{ width: 240, flexShrink: 0, borderRight: 1, borderColor: "divider", bgcolor: "white" }}>
                <SidebarMenu pageName="on6" />
            </Box>

            {/* Main Content */}
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                {/* AppBar */}
                <AppBar position="sticky" color="default" elevation={1}>
                    <Toolbar>
                        <IconButton edge="start" onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                            On6 — Detalle del cliente
                        </Typography>
                    </Toolbar>
                </AppBar>

                {/* Scrollable Content */}
                <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
                        {/* Client Card */}
                        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                            <Grid container spacing={3} alignItems="flex-start">
                                {/* Avatar y info básica */}
                                <Grid size={12}>
                                    <Stack alignItems="center" spacing={2}>
                                        <Avatar
                                            sx={{
                                                width: 100,
                                                height: 100,
                                                bgcolor: "primary.main",
                                                fontSize: "2.5rem",
                                            }}
                                        >
                                            <PersonIcon fontSize="large" />
                                        </Avatar>
                                        <Box textAlign="center">
                                            <Typography variant="h6" fontWeight={600}>
                                                {client?.CardName ?? "Sin nombre"}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {client?.CardFName ?? ""}
                                            </Typography>
                                            <Chip
                                                label={`Código: ${client?.CardCode ?? "—"}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ mt: 1 }}
                                            />
                                        </Box>
                                    </Stack>
                                </Grid>

                                {/* Información de contacto */}
                                <Grid size={12}>
                                    <Stack spacing={1.5}>
                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                            Información de contacto
                                        </Typography>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <PersonIcon fontSize="small" color="action" />
                                            <Typography variant="body2">{client?.CntctPrsn ?? "—"}</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <PhoneIcon fontSize="small" color="action" />
                                            <Typography variant="body2">{client?.Phone1 ?? "—"}</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <BusinessIcon fontSize="small" color="action" />
                                            <Typography variant="body2">NIT: {client?.U_NIT ?? "—"}</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <BusinessIcon fontSize="small" color="action" />
                                            <Typography variant="body2">Segmento: {client?.U_seg_cli ?? "—"}</Typography>
                                        </Stack>
                                    </Stack>
                                </Grid>

                                {/* Detalles rápidos */}
                                <Grid size={12}>
                                    <Stack spacing={1.5}>
                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                            Detalles
                                        </Typography>
                                        <Stack direction="row" spacing={2}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    NRC
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {client?.AddID ?? "—"}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Ruta
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {client?.U_Ruta ?? "—"}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Estado
                                                </Typography>
                                                <Chip
                                                    label={client?.enabled ? "Activo" : "Inactivo"}
                                                    size="small"
                                                    color={client?.enabled ? "success" : "error"}
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </Stack>
                                        <Divider sx={{ my: 1 }} />
                                        <Stack direction="row" spacing={2}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Condición pago
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {client?.PymntGroup ?? "—"}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Balance
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500} color="primary">
                                                    ${typeof client?.Balance === "number" ? client.Balance.toFixed(2) : "—"}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Stack>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            {/* Dirección y acciones */}
                            <Grid container spacing={2} alignItems="center">
                                <Grid size={12}>
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <LocationOnIcon color="action" sx={{ mt: 0.5 }} />
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Dirección
                                            </Typography>
                                            <Typography variant="body2">{client?.Address ?? "—"}</Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid size={12} textAlign="right">
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        {mapsHref && (
                                            <MuiLink
                                                href={mapsHref}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <Button size="small" startIcon={<MapIcon />}>
                                                    Ver mapa
                                                </Button>
                                            </MuiLink>
                                        )}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Estadísticas */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid size={4}>
                                <Card>
                                    <CardContent>
                                        <Typography color="text.secondary" gutterBottom>
                                            Total visitas
                                        </Typography>
                                        <Typography variant="h5" sx={{ color: "primary.main" }}>
                                            {stats.totalVisits}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Entradas registradas
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid size={4}>
                                <Card>
                                    <CardContent>
                                        <Typography color="text.secondary" gutterBottom>
                                            Promedio
                                        </Typography>
                                        <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                            <Typography variant="h5" sx={{ color: "primary.main" }}>
                                                {stats.avgScore.toFixed(1)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                /5
                                            </Typography>
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary">
                                            Basado en {stats.totalVisits} visitas
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid size={4}>
                                <Card>
                                    <CardContent>
                                        <Typography color="text.secondary" gutterBottom>
                                            Peor calificación
                                        </Typography>
                                        {stats.lowest ? (
                                            <Box>
                                                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                                    <Typography variant="h6" sx={{ color: "error.main" }}>
                                                        {stats.lowest.score}/5
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {stats.lowest.restaurantName ?? stats.lowest.restaurantId ?? "—"}
                                                    </Typography>
                                                </Stack>
                                                {stats.lowest.comment && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        "{stats.lowest.comment}"
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Sin datos
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Calificaciones */}
                        <Box>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Calificaciones registradas
                            </Typography>
                            {loadingRatings ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                                    <CircularProgress />
                                </Box>
                            ) : ratings.length === 0 ? (
                                <Paper sx={{ p: 3, textAlign: "center" }}>
                                    <Typography color="text.secondary">No se encontraron calificaciones.</Typography>
                                </Paper>
                            ) : (
                                <Grid container spacing={2}>
                                    {ratings.map((r) => (
                                        <Grid size={12} key={r.id}>
                                            <CardRating
                                                id={r.id}
                                                cardCode={r.restaurantId ?? ""}
                                                cardName={r.restaurantName ?? ""}
                                                comment={r.comment ?? ""}
                                                score={r.score ?? 0}
                                                createdAt={r.createdAt ?? ""}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default OnSixCostumer;